import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "pt-2 space-y-4" }
const _hoisted_2 = { class: "rounded-md border border-gray-200 px-5 py-3 opacity-60" }
const _hoisted_3 = { class: "text-gray-500 text-sm font-medium" }
const _hoisted_4 = {
  key: 0,
  class: "rounded-md border border-gray-200 px-4 py-2 opacity-60"
}
const _hoisted_5 = { class: "text-gray-500 text-sm font-medium" }
const _hoisted_6 = { class: "rounded-md border border-gray-200 px-4 py-2 opacity-60" }
const _hoisted_7 = { class: "text-gray-500 text-sm font-medium" }
const _hoisted_8 = { class: "rounded-md border border-gray-200 px-4 py-2 opacity-60" }
const _hoisted_9 = { class: "text-gray-500 text-sm font-medium" }
const _hoisted_10 = {
  key: 1,
  class: "rounded-md border border-gray-200 px-4 py-2 opacity-60"
}
const _hoisted_11 = { class: "text-gray-500 text-sm font-medium" }
const _hoisted_12 = { class: "rounded-md border border-gray-200 px-4 py-2 opacity-60" }
const _hoisted_13 = { class: "text-gray-500 text-sm font-medium" }

import { computed, ref, watch } from 'vue';
import { VisuallyHidden } from 'radix-vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';
import CustomInput from '@/components/ui/CustomInput.vue';
import CustomButton from '@/components/ui/CustomButton.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { i18n } from '@/plugins/internationalization/i18n';
import ApiClient from '@/plugins/store/actions/api/ApiClient';
import { queryKeys } from '@/plugins/store/actions/queries/study-periods/index';

interface CreateState {
    comparisonType: 'within' | 'between';
    categoryIds: number[];
    animalGroupId?: number;
    studyPeriodIds: number[];
    favoriteTeamIds: number[];
    growthReferenceId?: number;
}

interface Props {
    comparisonType: 'within' | 'between';
    categoryNames: string[];
    animalGroupName: string;
    studyPeriodNames: string[];
    favoriteTeamNames: string[];
    growthReferenceName: string;
    state: CreateState;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SaveAnalysisDialog',
  props: /*@__PURE__*/_mergeModels({
    comparisonType: {},
    categoryNames: {},
    animalGroupName: {},
    studyPeriodNames: {},
    favoriteTeamNames: {},
    growthReferenceName: {},
    state: {}
  }, {
    "open": { type: Boolean, ...{ required: true } },
    "openModifiers": {},
  }),
  emits: ["update:open"],
  setup(__props: any) {

const props = __props;

const toast = useToast();

const displayComparisonType = computed(() => {
    switch (props.comparisonType) {
        case 'within':
            return i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.singleTeam.label');
        case 'between':
            return i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.multipleTeams.label');
        default:
            return '';
    }
});

const isOpen = _useModel<boolean>(__props, 'open');

const name = ref('');

watch(isOpen, (newVal) => {
    if (!newVal) {
        name.value = '';
    }
});

const isFormValid = computed(() => name.value.length > 0);

const isLoading = ref(false);

const queryClient = useQueryClient();

async function createStudyPeriodSetup() {
    try {
        if (isLoading.value) {
            return;
        }

        isLoading.value = true;

        await ApiClient.send(
            'post',
            'monitor/study-periods/setup',
            {
                skipHandleError: true,
            },
            {
                name: name.value,
                type: props.state.comparisonType,
                animal_group_id: props.state.animalGroupId,
                categories: props.state.categoryIds,
                study_periods: props.state.studyPeriodIds,
                favorites: props.state.favoriteTeamIds,
                growth_reference: props.state.growthReferenceId,
            }
        );

        await queryClient.invalidateQueries({ queryKey: queryKeys.getStudyPeriodSetups() });

        toast.success(i18n.global.t('views.monitoring.teamComparison.dialogs.saveAnalysis.successMessage'));

        isOpen.value = false;
    } catch (err) {
        toast.error(i18n.global.t('views.monitoring.teamComparison.dialogs.saveAnalysis.errorMessage'));
    } finally {
        isLoading.value = false;
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    modelValue: isOpen.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((isOpen).value = $event)),
    disabled: isLoading.value
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(DialogHeader), null, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogTitle), null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.title')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(VisuallyHidden), null, {
            default: _withCtx(() => [
              _createVNode(_unref(DialogDescription), null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.description')), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(CustomInput, {
          value: name.value as any,
          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((name.value as any) = $event)),
          rules: [{ validate: (value) => value.length > 0, message: '' }],
          required: "",
          label: _ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.name.label')
        }, null, 8, ["value", "rules", "label"]),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.comparisonType.label')), 1),
          _createElementVNode("span", null, _toDisplayString(displayComparisonType.value), 1)
        ]),
        (_ctx.categoryNames.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.categories.label')), 1),
              _createElementVNode("span", null, _toDisplayString(_ctx.categoryNames.join(', ')), 1)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.animalGroup.label')), 1),
          _createElementVNode("span", null, _toDisplayString(_ctx.animalGroupName), 1)
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.teams.label')), 1),
          _createElementVNode("span", null, _toDisplayString(_ctx.studyPeriodNames.join(', ')), 1)
        ]),
        (_ctx.favoriteTeamNames.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.favoriteTeams.label')), 1),
              _createElementVNode("span", null, _toDisplayString(_ctx.favoriteTeamNames.join(', ')), 1)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_12, [
          _createElementVNode("div", _hoisted_13, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.growthReference.label')), 1),
          _createElementVNode("span", null, _toDisplayString(_ctx.growthReferenceName), 1)
        ])
      ]),
      _createVNode(_unref(DialogFooter), { class: "flex sm:justify-center pt-2" }, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogClose), { "as-child": "" }, {
            default: _withCtx(() => [
              _createVNode(CustomButton, {
                "color-preset": "white",
                class: "min-w-44",
                disabled: isLoading.value
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.buttons.cancel')), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            _: 1
          }),
          _createVNode(CustomButton, {
            class: "min-w-44",
            loading: isLoading.value,
            disabled: !isFormValid.value,
            onClick: createStudyPeriodSetup
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.dialogs.saveAnalysis.buttons.save')), 1)
            ]),
            _: 1
          }, 8, ["loading", "disabled"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue", "disabled"]))
}
}

})